import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { HiArrowUp } from 'react-icons/hi2';
import { EASE } from '../lib/motion';

const links = ['work', 'services', 'process', 'studio', 'contact'] as const;
const WORDMARK = 'kouk.gr'.split('');

export function Footer() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();
  const year = new Date().getFullYear();

  return (
    <footer
      id="colophon"
      className="relative overflow-hidden border-t border-line px-5 pt-16 pb-8 md:px-8"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <p className="max-w-[40ch] text-sm text-muted">
            {t('footer.tagline')}
          </p>

          <nav aria-label="Footer" className="flex flex-wrap gap-x-8 gap-y-3">
            {links.map((id) => (
              <a
                key={id}
                href={`#${id}`}
                className="text-sm text-muted transition-colors hover:text-fg"
              >
                {t(`nav.${id}`)}
              </a>
            ))}
          </nav>

          <a
            href="#top"
            className="group inline-flex items-center gap-2 self-start rounded-full border border-line-strong px-4 py-2 text-sm text-muted transition-colors hover:border-accent hover:text-accent"
          >
            {t('footer.top')}
            <HiArrowUp className="transition-transform duration-300 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {/* Full-bleed wordmark, letters rise in sequence as the page ends.
            The trigger sits on the unclipped row, not the masked letters. */}
        <motion.p
          aria-hidden
          initial={reduce ? false : 'hidden'}
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.05 } },
          }}
          className="-mx-[0.02em] mt-16 flex select-none font-display text-[min(calc((100vw-4.5rem)/3.02),29rem)] font-semibold leading-[0.8] tracking-[-0.065em] md:mt-20"
        >
          {WORDMARK.map((ch, i) => (
            <span key={i} className="inline-block shrink-0   pb-[0.06em]">
              <motion.span
                className={`inline-block ${i >= 4 ? 'text-accent' : 'text-fg'}`}
                variants={{
                  hidden: { y: '100%' },
                  show: { y: '0%', transition: { duration: 1.1, ease: EASE } },
                }}
              >
                {ch}
              </motion.span>
            </span>
          ))}
        </motion.p>

        <div className="mt-6 flex flex-col gap-2 border-t border-line pt-6 text-xs text-faint sm:flex-row sm:items-center sm:justify-between">
          <span>
            © {year} kouk.gr. {t('footer.rights')}
          </span>
          <span>Athens, Greece</span>
        </div>
      </div>
    </footer>
  );
}
